/**
 * Self-serve sign-up on /auth: what goes into `supabase.auth.signUp`, where
 * the confirmation link returns to, and where a fresh session lands.
 * Pure, so vitest covers it (src/lib/auth/__tests__/signup.test.ts).
 */
import type { PlanId } from "@/lib/pricing/plans";
import { termsAcceptance, type TermsAcceptance } from "@/lib/legal/terms/version";

export type SignupLanguage = "cs" | "en";

/**
 * User metadata stored with the account. `handle_new_user` (prod trigger)
 * reads `full_name` and `company_name` to create the person's company with
 * them as owner; `language` picks the trial reminder e-mails' language.
 */
export interface SignupMetadata extends TermsAcceptance {
  full_name: string;
  company_name: string;
  language: SignupLanguage;
  /** The plan chosen on the landing page, if any (analytics only). */
  plan_intent: PlanId | null;
  /** The B2B declaration in the terms checkbox ("Jsem podnikatel"). */
  business_declared: true;
}

export function signupMetadata(
  input: { fullName: string; company: string; language: SignupLanguage; plan: PlanId | null },
  now: Date = new Date()
): SignupMetadata {
  return {
    full_name: input.fullName.trim(),
    company_name: input.company.trim(),
    language: input.language,
    plan_intent: input.plan,
    business_declared: true,
    ...termsAcceptance(now),
  };
}

/**
 * The e-mail confirmation link returns to /auth in the same language and on
 * the same host: the page picks up the session there and sends the person on
 * (afterAuthPath). The plan rides along so it survives a new tab or another
 * browser, and so does a same-site `next`: the MCP consent sign-in on
 * zettlapp.com (`?next=/oauth/…`) must come back there, and zettlapp.com
 * keeps only URLs with `next=/oauth` or `code=` instead of redirecting them.
 */
export function signupRedirectUrl(
  origin: string,
  language: SignupLanguage,
  plan: PlanId | null,
  next: string | null = null
): string {
  const query = new URLSearchParams();
  if (plan) query.set("plan", plan);
  if (isSameSitePath(next)) query.set("next", next);
  const search = query.toString();
  return `${origin}/${language}/auth${search ? `?${search}` : ""}`;
}

/**
 * A path on this site. Browsers treat a backslash like a slash and drop
 * tabs and newlines, so "/\\evil.example" and "/\t/evil.example" are
 * protocol-relative URLs to another host; they are rejected along with "//".
 * The final check resolves the path the way the browser will.
 */
function isSameSitePath(path: string | null): path is string {
  if (!path || !path.startsWith("/") || path.startsWith("//")) return false;
  if (path.includes("\\") || [...path].some((c) => c.charCodeAt(0) < 0x20)) return false;
  try {
    const base = "https://same-site.invalid";
    return new URL(path, base).origin === base;
  } catch {
    return false;
  }
}

/**
 * Where a session lands. A same-site `?next=` wins (a crafted `//host` or
 * absolute URL is ignored); everyone else goes to the dashboard, where a new
 * company's first-run checklist and the trial banner show. A plan chosen on the
 * website no longer opens Settings → Předplatné first: it stays remembered for
 * the tab (planIntent) and is preselected when the trial converts, but a new
 * company needs its bank and documents connected before paying makes sense.
 */
export function afterAuthPath(language: SignupLanguage, next: string | null): string {
  if (isSameSitePath(next)) return next;
  return `/${language}/dashboard`;
}

export type SignupErrorKind =
  | "captchaFailed"
  | "userExists"
  | "signupDisabled"
  | "weakPassword"
  | "rateLimited"
  | "invalidEmail"
  | "other";

/** Supabase Auth error codes on sign-up, mapped to the message shown. */
export function signupErrorKind(code: string | undefined): SignupErrorKind {
  switch (code) {
    case "captcha_failed":
      return "captchaFailed";
    case "user_already_exists":
    case "email_exists":
      return "userExists";
    case "signup_disabled":
    case "email_provider_disabled":
      return "signupDisabled";
    case "weak_password":
      return "weakPassword";
    case "over_email_send_rate_limit":
    case "over_request_rate_limit":
      return "rateLimited";
    case "email_address_invalid":
    case "validation_failed":
      return "invalidEmail";
    default:
      return "other";
  }
}

export type ReturnNotice = "confirmed" | "linkInvalid" | null;

/**
 * What to tell someone who came back from the confirmation link without a
 * session. Supabase confirms the address before redirecting, so a `code`
 * that could not be exchanged (opened in another browser, which lacks the
 * PKCE verifier) still means "confirmed, now sign in". An `error_code`
 * (expired or reused link) arrives in the query or the hash.
 */
export function returnNotice(search: string, hash: string): ReturnNotice {
  const query = new URLSearchParams(search);
  const fragment = new URLSearchParams(hash.replace(/^#/, ""));
  if (query.get("error_code") || fragment.get("error_code") || query.get("error")) {
    return "linkInvalid";
  }
  if (query.get("code")) return "confirmed";
  return null;
}

/** The slice of a Supabase user the confirmation check reads. */
export interface ConfirmedUserLike {
  email_confirmed_at?: string | null;
  app_metadata?: { provider?: string } | null;
}

/**
 * Whether this session arrived from the confirmation e-mail's link: a `code`
 * in the URL, an e-mail account, confirmed in the last 15 minutes. A Google or
 * Microsoft return also carries a `code`, so the provider decides; an old
 * account signing in again was confirmed long ago.
 */
export function cameFromConfirmationLink(
  search: string,
  user: ConfirmedUserLike | null | undefined,
  now: Date = new Date()
): boolean {
  if (!new URLSearchParams(search).get("code") || !user?.email_confirmed_at) return false;
  if ((user.app_metadata?.provider ?? "email") !== "email") return false;
  const age = now.getTime() - new Date(user.email_confirmed_at).getTime();
  return age >= 0 && age <= 15 * 60 * 1000;
}
